import generalKnowledge from "./generalKnowledge";
import passenger from "./passenger";
import airBrakes from "./airBrakes";
import schoolBus from "./schoolBus";
import combinationVehicles from "./combinationVehicles";
import hazmat from "./hazmat";
import tanker from "./tanker";
import doublesTriples from "./doublesTriples";

export const SECTIONS = {
  GENERAL: "General Knowledge",
  PASSENGER: "Passenger Endorsement",
  AIR_BRAKES: "Air Brakes",
  SCHOOL_BUS: "School Bus (S) Endorsement",
  COMBINATION: "Combination Vehicles",
  HAZMAT: "Hazardous Materials",
  TANKER: "Tanker Vehicles",
  DOUBLES_TRIPLES: "Doubles & Triples",
};

export const QUESTION_BANKS = {
  [SECTIONS.GENERAL]: generalKnowledge,
  [SECTIONS.PASSENGER]: passenger,
  [SECTIONS.AIR_BRAKES]: airBrakes,
  [SECTIONS.SCHOOL_BUS]: schoolBus,
  [SECTIONS.COMBINATION]: combinationVehicles,
  [SECTIONS.HAZMAT]: hazmat,
  [SECTIONS.TANKER]: tanker,
  [SECTIONS.DOUBLES_TRIPLES]: doublesTriples,
};

// Exam mode: how many questions to draw per section
export const EXAM_CONFIG = {
  [SECTIONS.GENERAL]: { count: 50, timeMinutes: 60 },
  [SECTIONS.PASSENGER]: { count: 20, timeMinutes: 25 },
  [SECTIONS.AIR_BRAKES]: { count: 25, timeMinutes: 30 },
  [SECTIONS.SCHOOL_BUS]: { count: 20, timeMinutes: 25 },
  [SECTIONS.COMBINATION]: { count: 20, timeMinutes: 25 },
  [SECTIONS.HAZMAT]: { count: 30, timeMinutes: 40 },
  [SECTIONS.TANKER]: { count: 20, timeMinutes: 25 },
  [SECTIONS.DOUBLES_TRIPLES]: { count: 20, timeMinutes: 25 },
};

// Practice mode: use all questions, with section-appropriate timing
export const PRACTICE_CONFIG = {
  [SECTIONS.GENERAL]: { timeMinutes: 90 },
  [SECTIONS.PASSENGER]: { timeMinutes: 60 },
  [SECTIONS.AIR_BRAKES]: { timeMinutes: 60 },
  [SECTIONS.SCHOOL_BUS]: { timeMinutes: 60 },
  [SECTIONS.COMBINATION]: { timeMinutes: 60 },
  [SECTIONS.HAZMAT]: { timeMinutes: 75 },
  [SECTIONS.TANKER]: { timeMinutes: 60 },
  [SECTIONS.DOUBLES_TRIPLES]: { timeMinutes: 60 },
};

export const PASS_THRESHOLD = 80; // percent

// Sections required for MBTA Class B CDL (Bus Operator)
export const MBTA_SECTIONS = [
  SECTIONS.GENERAL,
  SECTIONS.AIR_BRAKES,
  SECTIONS.PASSENGER,
];

// Extra endorsement sections (not required for MBTA Class B)
export const EXTRA_SECTIONS = [
  SECTIONS.SCHOOL_BUS,
  SECTIONS.COMBINATION,
  SECTIONS.HAZMAT,
  SECTIONS.TANKER,
  SECTIONS.DOUBLES_TRIPLES,
];

/** Fisher-Yates shuffle — returns a new shuffled array */
export function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Pick `n` random questions from the bank for the given section.
 * Also shuffles the answer options within each question and adjusts the answer index.
 */
export function pickQuestions(section, count) {
  const bank = QUESTION_BANKS[section];
  const pool = shuffle(bank).slice(0, Math.min(count, bank.length));
  return pool.map((q) => shuffleOptions(q));
}

/** Shuffle a question's options and update the answer index accordingly */
function shuffleOptions(q) {
  const indexed = q.options.map((opt, i) => ({ opt, isAnswer: i === q.answer }));
  const shuffled = shuffle(indexed);
  return {
    ...q,
    options: shuffled.map((x) => x.opt),
    answer: shuffled.findIndex((x) => x.isAnswer),
  };
}

/** Get ALL questions for practice mode (shuffled, with options shuffled) */
export function getAllQuestions(section) {
  return shuffle(QUESTION_BANKS[section]).map((q) => shuffleOptions(q));
}

/** MBTA General Knowledge drill questions (gk246–gk316, gk320–gk352) */
export function getMbtaGKQuestions() {
  return shuffle(
    QUESTION_BANKS[SECTIONS.GENERAL].filter((q) => {
      const n = parseInt(q.id.replace("gk", ""), 10);
      return (n >= 246 && n <= 316) || (n >= 320 && n <= 352);
    })
  ).map((q) => shuffleOptions(q));
}

/** MBTA Air Brakes drill questions (ab076–ab138) */
export function getMbtaAirBrakesQuestions() {
  return shuffle(
    QUESTION_BANKS[SECTIONS.AIR_BRAKES].filter((q) => {
      const n = parseInt(q.id.replace("ab", ""), 10);
      return n >= 76 && n <= 138;
    })
  ).map((q) => shuffleOptions(q));
}

/** MBTA Passenger drill questions (pa076–pa123) */
export function getMbtaPassengerQuestions() {
  return shuffle(
    QUESTION_BANKS[SECTIONS.PASSENGER].filter((q) => {
      const n = parseInt(q.id.replace("pa", ""), 10);
      return n >= 76 && n <= 123;
    })
  ).map((q) => shuffleOptions(q));
}

/** All MBTA training-drill questions combined (gk246–gk316 + ab076–ab115 + pa076–pa110) */
export function getMbtaTrainingQuestions() {
  return shuffle([
    ...getMbtaGKQuestions(),
    ...getMbtaAirBrakesQuestions(),
    ...getMbtaPassengerQuestions(),
  ]);
}

export const SECTION_META = {
  [SECTIONS.GENERAL]: {
    icon: "🚛",
    color: "#1a56db",
    lightColor: "#eff6ff",
    borderColor: "#bfdbfe",
    bankSize: generalKnowledge.length,
    topics: ["Vehicle Inspection", "Braking & Stopping", "Speed & Space", "Weather & Adverse Conditions", "Hours of Service", "ELD Requirements", "Hazmat", "Emergencies", "CDL Classes & Endorsements", "Weight & Bridge Laws", "Fatigue & Distraction", "Skid Control", "Mountain Driving", "Massachusetts Regulations", "Non-Domiciled CDL (2026)", "Roadside Inspections", "Cargo Securement", "Defensive Driving", "Collision Avoidance", "Visibility & Mirrors", "Tire Safety", "Turning & Off-Tracking", "Work Zones"],
  },
  [SECTIONS.PASSENGER]: {
    icon: "🚌",
    color: "#0891b2",
    lightColor: "#ecfeff",
    borderColor: "#a5f3fc",
    bankSize: passenger.length,
    topics: ["Pre-Trip Inspection", "Loading/Unloading", "Railroad Crossings", "Emergency Evacuation", "Hazmat on Buses", "ADA & Wheelchair Operations", "Accident Procedures", "Fueling Safety", "Passenger Management", "Night Operations", "Security", "MBTA Operations", "Drawbridge Procedures", "Door Interlock Systems", "Standee Rules", "Medical Emergencies"],
  },
  [SECTIONS.AIR_BRAKES]: {
    icon: "⚙️",
    color: "#7c3aed",
    lightColor: "#f5f3ff",
    borderColor: "#ddd6fe",
    bankSize: airBrakes.length,
    topics: ["System Components", "S-Cam & Foundation Brakes", "Wedge & Disc Brakes", "Dual Air Brakes", "Inspection & Testing", "Spring Brakes", "Brake Fade & Heat", "ABS", "Combination Vehicles", "Air Dryer & Moisture", "Tractor Protection Valve", "Slack Adjusters", "Emergency Brake System", "Glad Hands & Air Lines", "Anti-Compounding Valve"],
  },
  [SECTIONS.SCHOOL_BUS]: {
    icon: "🚸",
    color: "#d97706",
    lightColor: "#fffbeb",
    borderColor: "#fde68a",
    bankSize: schoolBus.length,
    topics: ["Danger Zones", "Loading & Unloading Students", "Stop Arm & Flashing Lights", "Mirror Systems", "Railroad Crossings", "Emergency Evacuation", "Post-Trip Inspection", "Pupil Management", "Safe Haven", "ABS Braking", "Backing a School Bus", "Student Crossing Procedures", "Pre-Trip Inspection", "S Endorsement Requirements"],
  },
  [SECTIONS.COMBINATION]: {
    icon: "🚚",
    color: "#b45309",
    lightColor: "#fffbeb",
    borderColor: "#fde68a",
    bankSize: combinationVehicles.length,
    topics: ["Fifth Wheel & Kingpin", "Coupling Procedure", "Uncoupling Procedure", "Glad Hands & Air Lines", "Tractor Protection Valve", "Jackknife Prevention", "Rollover Prevention", "Cargo Weight Distribution", "Backing", "Turning & Off-Tracking", "Combination Braking", "High Wind Operations"],
  },
  [SECTIONS.HAZMAT]: {
    icon: "☣️",
    color: "#dc2626",
    lightColor: "#fef2f2",
    borderColor: "#fecaca",
    bankSize: hazmat.length,
    topics: ["Hazmat Classes 1–9", "Shipping Papers", "Placards & Labels", "Markings & ID Numbers", "Driver Responsibilities", "Segregation & Compatibility", "Route Restrictions", "Attending & Parking", "Emergency Procedures", "Packing Groups", "Security Plans", "Hazmat Endorsement Requirements"],
  },
  [SECTIONS.TANKER]: {
    icon: "🛢️",
    color: "#0f766e",
    lightColor: "#f0fdfa",
    borderColor: "#99f6e4",
    bankSize: tanker.length,
    topics: ["Liquid Surge", "Baffled vs Unbaffled Tanks", "High Center of Gravity", "Outage & Filling", "Tank Inspection", "Emergency Shutoffs", "Driving Techniques", "Rollover Prevention", "Flammable Liquid Safety", "Pressurized & Cryogenic Tanks", "Tank Endorsement", "X Endorsement (Tank + Hazmat)"],
  },
  [SECTIONS.DOUBLES_TRIPLES]: {
    icon: "🚛",
    color: "#6d28d9",
    lightColor: "#f5f3ff",
    borderColor: "#c4b5fd",
    bankSize: doublesTriples.length,
    topics: ["Coupling Doubles & Triples", "Uncoupling Procedure", "Converter Dolly Inspection", "Air Systems", "Rearward Amplification", "Crack-the-Whip Effect", "Braking Distances", "Off-Tracking", "Rollover Risk", "High Wind Operations", "Parking & Securing", "State & Federal Regulations"],
  },
};
